//region :: Imports ::
import {ReactNode, MouseEvent, ReactElement, RefObject} from 'react';
import {Dropdown} from 'primereact/dropdown';
import {DataTable, DataTableExportFunctionParams, DataTablePFSEvent, DataTableProps, DataTableSortMeta, DataTableSortModeType} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {FilterMatchMode} from 'primereact/api';
import {PaginatorTemplate} from 'primereact/paginator';
import MButton from '../../Toolkit/Components/Controls/Button';
import {evalulateBool, MBoolean} from '../../Toolkit/Components/Controls/Control';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import {_, copyToClipboard, isNullOrUndefined} from '../../Toolkit/Utils';
import {MInfoIcon, MIcon, MNewIconName, MOpenIconName, MRefreshIconName, MDeleteIconName} from '../../Toolkit/Icons';

//endregion

/** */
export type MTableProps<TRowData extends object> = {
    rowData: TRowData[];
    header?: MTableHeader;
    filters?: MTableFilters;
    loading?: boolean;
    totalRows?: number;
    disabled?: MBoolean;
    rowGroupMode?: string;
    groupRowsBy?: string;
    innerRef?: RefObject<DataTable>;
    exportName?: string
}

/** */
export type MTableHeader = {
    buttons?: {
        standard?: MTableHeaderButtons;
        custom?: ReactElement<MButton>[];
    };
}

/** */
export enum MTableHeaderButtons {
    None = 0,
    New = 1 << 1,
    Open = 1 << 2,
    Refresh = 1 << 3,
    Delete = 1 << 4,
    All = New | Open | Refresh | Delete
}

/** */
export type MTableFilters = {
    [key: string]: {
        value: any;
        matchMode: FilterMatchMode;
    };
}

/** */
type Props<TRowData extends object> = MTableProps<TRowData> & {
    rowKey: string;
    rowsPerPage?: number;
    rowsPerPageOptions?: number[];
    canShowAllRows?: boolean;
    showSelection?: boolean;
    multipleSelection?: boolean;
    sortField?: string;
    sortOrder?: 1 | -1;
    multiSortMeta?: DataTableSortMeta[];
    sortMode?: DataTableSortModeType;
    lazy?: boolean;
    noRowsIcon?: MIcon;
    noRowsMessage?: string | ReactNode;
    showRowsPerPageDropdown?: boolean;
    showPrevPageLink?: boolean;
    showPageLinks?: boolean;
    showNextPageLink?: boolean;
    showCurrentPageReport?: boolean;
    children?: ReactNode | ReactNode[];
    onSelectionChange?: (selection: TRowData | TRowData[] | undefined) => void;
    onRowsChange?: (rowData: TRowData[]) => void;
    onPageChange?: (event: DataTablePFSEvent) => Promise<void>;
    onNewClick?: () => void | Promise<void>;
    onOpenClick?: () => void | Promise<void>;
    onRefreshClick?: () => void | Promise<void>;
    onDeleteClick?: () => void | Promise<void>;
    selection? : TRowData | TRowData[] | undefined;
    exportFunction?:(e: DataTableExportFunctionParams) => any;
    stateStorage?: 'local' | 'session';
    stateKey?: string;
}

/** */
type State<TRowData extends object> = {
    selection?: TRowData | TRowData[];
    isRefreshing: boolean;
    lazyOptions: {
        pageNumber: number,
        indexOfFirstRow: number,
    };
}

/** */
class MTable<TRowData extends object> extends MComponent<Props<TRowData>, State<TRowData>> {

    /** */
    public constructor(props: Props<TRowData>) {
        super(props);

        this.bindMethods();
        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.table_onRowsChange = this.table_onRowsChange.bind(this);
        this.table_onPageChange = this.table_onPageChange.bind(this);
        this.newButton_onClick = this.newButton_onClick.bind(this);
        this.openButton_onClick = this.openButton_onClick.bind(this);
        this.refreshButton_onClick = this.refreshButton_onClick.bind(this);
        this.deleteButton_onClick = this.deleteButton_onClick.bind(this);
        this.new = this.new.bind(this);
        this.refresh = this.refresh.bind(this);
        this.open = this.open.bind(this);
        this.delete = this.delete.bind(this);
        this.getHeaderTemplate = this.getHeaderTemplate.bind(this);
        this.getPaginatorTemplate = this.getPaginatorTemplate.bind(this);
        this.getNoRowsTemplate = this.getNoRowsTemplate.bind(this);

    }

    /** */
    private initializeState(): void {

        this.state = {
            isRefreshing: false,
            lazyOptions: {
                pageNumber: 0,
                indexOfFirstRow: 0
            }
        };

    }

    /** */
    private async newButton_onClick(event: MouseEvent<HTMLButtonElement>): Promise<void> {

        event.preventDefault();
        await this.new();

    }

    /** */
    private async openButton_onClick(event: MouseEvent<HTMLButtonElement>): Promise<void> {

        event.preventDefault();
        await this.open();

    }

    /** */
    private async refreshButton_onClick(event: MouseEvent<HTMLButtonElement>): Promise<void> {

        event.preventDefault();
        await this.refresh();

    }

    /** */
    private async deleteButton_onClick(event: MouseEvent<HTMLButtonElement>): Promise<void> {

        event.preventDefault();
        await this.delete();

    }

    /** */
    private table_onSelectionChange(tableProps: DataTableProps): void {

        const selectedRow = tableProps.value === null ? undefined : tableProps.value as TRowData | TRowData[];
        this.safeSetState((draft: State<TRowData>) => draft.selection = selectedRow);
        this.props.onSelectionChange?.(selectedRow);

    }

    /** */
    private table_onRowsChange(rowData: TRowData[]): void {

        this.safeSetState((draft: State<TRowData>) => draft.selection = undefined);
        this.props.onSelectionChange?.(undefined);
        this.props.onRowsChange?.(rowData);

    }

    /** */
    private async table_onPageChange(event: DataTablePFSEvent): Promise<void> {

        try {

            await this.props.onPageChange?.(event);

        } finally {

            this.safeSetState((draft: State<TRowData>) => {

                draft.lazyOptions.pageNumber = event.page === undefined ? 0 : event.page;
                draft.lazyOptions.indexOfFirstRow = event.first;

            });

        }

    }

    /** */
    private getHeaderTemplate(): ReactNode | null {

        const standardButtons = this.props.header?.buttons?.standard ?? MTableHeaderButtons.None;
        const customButtons = this.props.header?.buttons?.custom;
        const allButtons: ReactElement<MButton>[] = [];

        if(customButtons !== undefined && customButtons.length > 0) {
            allButtons.push(...customButtons);
        }

        //  New
        if((standardButtons & MTableHeaderButtons.New) === MTableHeaderButtons.New) {
            allButtons.push(<MButton key="mtable-newButton"
                                     icon={MNewIconName}
                                     iconOnly={true}
                                     disabled={this.isNewDisabled}
                                     onClick={this.newButton_onClick}/>);
        }

        //  Open
        if((standardButtons & MTableHeaderButtons.Open) === MTableHeaderButtons.Open) {
            allButtons.push(<MButton key="mtable-openButton"
                                     icon={MOpenIconName}
                                     iconOnly={true}
                                     disabled={this.isOpenDisabled}
                                     onClick={this.openButton_onClick}/>);
        }

        //  Refresh
        if((standardButtons & MTableHeaderButtons.Refresh) === MTableHeaderButtons.Refresh) {
            allButtons.push(<MButton key="mtable-refreshButton"
                                     icon={MRefreshIconName}
                                     iconOnly={true}
                                     disabled={this.isRefreshDisabled}
                                     onClick={this.refreshButton_onClick}/>);
        }

        //  Delete
        if((standardButtons & MTableHeaderButtons.Delete) === MTableHeaderButtons.Delete) {
            allButtons.push(<MButton key="mtable-deleteButton"
                                     icon={MDeleteIconName}
                                     iconOnly={true}
                                     color="danger"
                                     disabled={this.isDeleteDisabled}
                                     onClick={this.deleteButton_onClick}/>);
        }

        if(allButtons.length === 0) {
            return null;
        }

        return (
            <div className="mtable-header">
                {
                    allButtons.map((button: ReactElement<MButton>, index: number) => {

                        const nodes = [] as ReactElement<MButton | MSpacer>[];

                        if(index > 0 && index < allButtons.length) {
                            nodes.push(<MSpacer key={`mtable_spacer_${index}`}/>);
                        }

                        nodes.push(button);
                        return nodes;

                    })
                }
            </div>
        );

    }

    /** */
    private getPaginatorTemplate(canShowAllRows: boolean): PaginatorTemplate {

        let layout = '';

        layout = `${layout} ${this.props.showRowsPerPageDropdown || this.props.showRowsPerPageDropdown === undefined ? 'RowsPerPageDropdown' : ''}`;
        layout = `${layout} ${this.props.showPrevPageLink || this.props.showPrevPageLink === undefined ? 'PrevPageLink' : ''}`;
        layout = `${layout} ${this.props.showPageLinks || this.props.showPageLinks === undefined ? 'PageLinks' : ''}`;
        layout = `${layout} ${this.props.showNextPageLink || this.props.showNextPageLink === undefined ? 'NextPageLink' : ''}`;
        layout = `${layout} ${this.props.showCurrentPageReport || this.props.showCurrentPageReport === undefined ? 'CurrentPageReport' : ''}`;

        return {

            layout: layout,
            RowsPerPageDropdown: templateOptions => {

                let options = [
                    {label: '5', value: 5},
                    {label: '10', value: 10},
                    {label: '20', value: 20},
                    {label: '50', value: 50}
                ];

                if(this.props.rowsPerPageOptions !== undefined && this.props.rowsPerPageOptions.length > 0) {

                    options = this.props.rowsPerPageOptions.map((value: number) => {
                        return {label: value.toString(), value: value};
                    });

                }

                if(canShowAllRows) {
                    options.push({label: 'All', value: templateOptions.totalRecords});
                }

                return (
                    <Dropdown options={options}
                              value={templateOptions.value}
                              onChange={templateOptions.onChange}/>
                );

            },
            FirstPageLink: undefined,
            PrevPageLink: undefined,
            PageLinks: undefined,
            NextPageLink: undefined,
            LastPageLink: undefined,
            CurrentPageReport: undefined,
            JumpToPageInput: undefined

        };

    }

    /** */
    private getNoRowsTemplate(): ReactNode {

        const icon = this.props.noRowsIcon !== undefined ? this.props.noRowsIcon : MInfoIcon;
        const isMessageString = typeof this.props.noRowsMessage === 'string';

        return (
            <div className="flex justify-content-center align-items-center my-1">
                {<span className={`pi ${icon.name} ${icon.color} text-2xl mr-3`}/>}
                {isMessageString ? <span>{this.props.noRowsMessage}</span> : this.props.noRowsMessage}
            </div>
        );

    }

    /** */
    public static getHeaderTemplate<TRowData extends object>(
        title: string, fieldName: string, selectedRow: any | any[] | undefined, canCopyValue: boolean) {

        let isCopyIconVisible = false;
        let valueToCopy = undefined as any | undefined;

        if(!isNullOrUndefined(selectedRow)) {

            if(!Array.isArray(selectedRow)) {
                isCopyIconVisible = true;
            } else {
                isCopyIconVisible = (selectedRow as TRowData[]).length > 0;
            }

        }

        if(isCopyIconVisible) {
            valueToCopy = selectedRow[fieldName];
        }

        return (
            <div className="flex justify-content-between align-items-center">
                <i className="text-lg pi pi-copy cursor-pointer mr-2"
                   style={{display: isCopyIconVisible && canCopyValue ? 'inherit' : 'none'}}
                   onClick={async() => await copyToClipboard(valueToCopy)}/>
                <div className="p-column-title mtext-wrap flex-grow-1">{title}</div>
            </div>
        );

    }

    /** */
    public componentDidUpdate(prevProps: Readonly<Props<TRowData>>, prevState: Readonly<State<TRowData>>, snapshot?: any): void {

        if(this.state.selection !== undefined) {

            //  We have a selection, so we now need to determine if we
            //  need to clear it. We will do this if the number of items
            //  in the array do not match or the order of the items has
            //  changed, i.e., the 'rowKey' properties do not match.
            let changed: boolean;

            if(this.props.rowData.length !== prevProps.rowData.length) {
                changed = true;
            } else {

                changed = _.some(this.props.rowData, (rowItem: TRowData, index: number) =>
                    _.get(rowItem, this.props.rowKey) !== _.get(prevProps.rowData[index], this.props.rowKey));

            }

            if(changed) {

                this.safeSetState((draft: State<TRowData>) => draft.selection = undefined);
                this.props.onSelectionChange?.(undefined);

            }

        }

    }

    /** */
    public componentDidMount(): void {

        if(this.props.lazy && this.props.totalRows === undefined) {
            throw new Error('The \'totalRows\' property must be set when lazy loading.');
        }

    }

    /** */
    public async new(): Promise<void> {

        if(this.props.onNewClick === undefined) {
            throw Error('Unable to execute the \'New\' request.');
        }

        await this.props.onNewClick();

    }

    /** */
    public async open(): Promise<void> {

        if(this.props.onOpenClick === undefined) {
            throw Error('Unable to execute the \'Open\' request.');
        }

        await this.props.onOpenClick?.();

    }

    /** */
    public async refresh(): Promise<void> {

        if(this.props.onRefreshClick === undefined) {
            throw Error('Unable to execute the \'Refresh\' request.');
        }

        if(this.state.isRefreshing) {
            return;
        }

        this.safeSetState(
            (draft: State<TRowData>) => draft.isRefreshing = true);

        try {
            await this.props.onRefreshClick?.();
        } finally {

            this.safeSetState(
                (draft: State<TRowData>) => draft.isRefreshing = false);

        }

    }

    /** */
    public async delete(): Promise<void> {

        if(this.props.onDeleteClick === undefined) {
            throw Error('Unable to execute the \'Delete\' request.');
        }

        await this.props.onDeleteClick?.();

    }

    /** */
    public render(): ReactElement {

        const lazyLoading = this.props.lazy ?? false;
        const rowsPerPage = this.props.rowsPerPage === undefined ? 10 : this.props.rowsPerPage;
        const totalRows = lazyLoading ? this.props.totalRows : this.props.rowData.length;
        const rowData = lazyLoading ? this.props.rowData.slice(this.state.lazyOptions.indexOfFirstRow, this.state.lazyOptions.indexOfFirstRow + rowsPerPage) : this.props.rowData;
        const canShowAllRows = this.props.canShowAllRows === undefined ? true : this.props.canShowAllRows;
        const filterDisplay = isNullOrUndefined(this.props.filters) ? undefined : 'row';
        const disableSelection = this.props.disabled === undefined ? false : evalulateBool(this.props.disabled);
        const loading = this.props.loading || this.state.isRefreshing;
        const showSelection = this.props.showSelection === undefined ? true : evalulateBool(this.props.showSelection);
        const selectionMode = this.props.multipleSelection !== undefined && this.props.multipleSelection ? 'multiple' : 'single';
        const sortField = this.props.sortField === undefined ? undefined : this.props.sortField;
        const sortOrder = this.props.sortOrder === undefined ? undefined : this.props.sortOrder;
        const indexOfFirstRow = lazyLoading ? this.state.lazyOptions.indexOfFirstRow : 0;

        return (
            <DataTable ref={this.props.innerRef}
                       dataKey={this.props.rowKey}
                       value={rowData}
                       rows={rowsPerPage}
                       totalRecords={totalRows}
                       size="small"
                       selectionMode={selectionMode}
                       selection={this.props.selection}
                       isDataSelectable={() => !disableSelection}
                       emptyMessage={this.getNoRowsTemplate()}
                       paginator={true}
                       paginatorTemplate={this.getPaginatorTemplate(canShowAllRows)}
                       sortField={sortField}
                       sortOrder={sortOrder}
                       multiSortMeta={this.props.multiSortMeta}
                       sortMode={this.props.sortMode}
                       filterDisplay={filterDisplay}
                       filters={this.props.filters}
                       resizableColumns={true}
                       columnResizeMode="fit"
                       header={this.getHeaderTemplate()}
                       loading={loading}
                       responsiveLayout="scroll"
                       stripedRows={true}
                       lazy={lazyLoading}
                       first={indexOfFirstRow}
                       onPage={lazyLoading ? this.table_onPageChange : undefined}
                       onSelectionChange={this.table_onSelectionChange}
                       onValueChange={this.table_onRowsChange}
                       rowGroupMode={this.props.rowGroupMode}
                       groupRowsBy={this.props.groupRowsBy}
                       exportFunction={this.props.exportFunction}
                       exportFilename={this.props.exportName}
                       stateStorage={this.props.stateStorage}
                       stateKey={this.props.stateKey}
            >
                {showSelection && <Column selectionMode={selectionMode} style={{width: '28px'}}/>}
                {this.props.children}
            </DataTable>
        );

    }

    /** */
    private get isNewDisabled(): boolean {
        return evalulateBool(this.props.disabled) || this.state.isRefreshing;
    }

    /** */
    private get isOpenDisabled(): boolean {

        return evalulateBool(this.props.disabled)
            || this.state.selection === undefined
            || (Array.isArray(this.state.selection) && (this.state.selection as TRowData[]).length === 0)
            || this.state.isRefreshing;

    }

    /** */
    private get isRefreshDisabled(): boolean {
        return evalulateBool(this.props.disabled) || this.state.isRefreshing;
    }

    /** */
    private get isDeleteDisabled(): boolean {
        return this.isOpenDisabled;
    }

}

export default MTable;
